<script setup lang="ts">
import { ref, computed } from 'vue';
import Product from "~/components/shop/Product.vue";
import Section from "~/components/layout/Section.vue";
import Container from "~/components/layout/Container.vue";
import Tags from "../../components/shop/Tags.vue";

const config = useRuntimeConfig()
const response = await GqlGetProducts();
const products = response.products;

const offersResponse = await GqlGetOffers();
const offers = offersResponse.offers;

const route = useRoute()

// Filter states
const selectedType = ref('');
const selectedLabel = ref('');
const isBundle = ref(false);
const showBundleFilter = ref(false);

// Get unique types and labels for filter options
const productTypes = computed(() => {
  const types = new Set(products.map(product => product.type));
  return Array.from(types).filter(Boolean);
});

const productLabels = computed(() => {
  const labels = new Set(products.map(product => product.label));
  return Array.from(labels).filter(Boolean);
});

// Check if any product is a bundle
showBundleFilter.value = products.some(product => product.subtype?.toLowerCase().includes('bundle') || product.title?.toLowerCase().includes('bundle'));

// Filtered products
const filteredProducts = computed(() => {
  return products.filter(product => {
    // Type filter
    if (selectedType.value && product.type !== selectedType.value) {
      return false;
    }
    
    // Label filter
    if (selectedLabel.value && product.label !== selectedLabel.value) {
      return false;
    }
    
    // Bundle filter
    if (isBundle.value && (!product.subtype?.toLowerCase().includes('bundle') && !product.title?.toLowerCase().includes('bundle'))) {
      return false;
    }
    
    return true;
  });
});

// Reset all filters
const resetFilters = () => {
  selectedType.value = '';
  selectedLabel.value = '';
  isBundle.value = false;
};

useHead({
	title: "Neuronoir | Shop",
	meta: [
		{name: 'description', content: 'Browse through our merchandise and other music related products. Dark and Heavy music made in Romania.'},
		{ name: 'keywords', content: 'music shop, shop, music, Romania' },
		{ property: 'og:title', content: 'Neuronoir | Shop' },
		{ property: 'og:description', content: 'Browse through our merchandise and other music related products. Dark and Heavy music made in Romania.' },
		{ property: 'og:url', content: 'https://www.neuronoir.art/shop' },
        { property: 'canonical', content: 'https://neuronoir.art' + route.path },
	],
})
</script>

<template>
	<Container class="bg-neurodarkgreyvar">
		<Section
			title="Shop"
			description="Browse through our merchandise and other music related products."
		>
            
            <!-- Filter controls -->
            <div class="mt-8 mb-6 p-4 bg-neurodarkgrey rounded-lg">
                <div class="grid md:grid-cols-4 grid-cols-1 gap-4">
                    <!-- Type filter -->
                    <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">Product Type</label>
                        <select v-model="selectedType" class="w-full bg-neurodarkgreyvar text-white border border-gray-700 rounded-md p-2">
                            <option value="">All Types</option>
                            <option v-for="type in productTypes" :key="type" :value="type">{{ type }}</option>
                        </select>
                    </div>
                    
                    <!-- Label filter -->
                    <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">Label</label>
                        <select v-model="selectedLabel" class="w-full bg-neurodarkgreyvar text-white border border-gray-700 rounded-md p-2">
                            <option value="">All Labels</option>
                            <option v-for="label in productLabels" :key="label" :value="label">{{ label }}</option>
                        </select>
                    </div>
                    
                    <!-- Bundle filter -->
                    <div v-if="showBundleFilter" class="flex items-center">
                        <label class="inline-flex items-center mt-5">
                            <input type="checkbox" v-model="isBundle" class="form-checkbox h-5 w-5">
                            <span class="ml-2 text-gray-300">Show Bundles Only</span>
                        </label>
                    </div>
                    
                    <!-- Reset button -->
                    <div class="flex items-center mt-5">
                        <button @click="resetFilters" class="px-4 py-2 text-red-500  rounded-md hover:text-red-700 transition-colors">
                            Reset Filters
                        </button>
                    </div>
                </div>
            </div>
        </Section>

		<div class="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-3 pb-20">
			<div class="col-span-1" v-for="product, i in filteredProducts" :key="product.productId" v-if="offers">
				<Product
					:offers="offers"
					:title="product.title"
					:type="product.type"
					:image-url="product.image.url"
					:brand="product.brand"
					:label="product.label"
					:slug="product.slug"
					:subtype="product.subtype"
					:productId="product.productId"
				></Product>
			</div>
		</div>
	</Container>
</template>
